import React, { useState, useEffect } from "react";
import axios from "axios";
import "./Category.css";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { MdOutlineDeleteOutline, MdEdit, MdSave, MdClose } from "react-icons/md";
import { FaPlus, FaImage } from "react-icons/fa";
import Swal from "sweetalert2";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ClipLoader from "react-spinners/ClipLoader";

const Category = () => {
  const [mainCategoryName, setMainCategoryName] = useState("");
  const [mainCategoryId, setMainCategoryId] = useState("");
  const [subCategoryName, setSubCategoryName] = useState("");
  const [mainCategoryImage, setMainCategoryImage] = useState(null);
  const [mainCategoryImagePreview, setMainCategoryImagePreview] = useState(null);
  const { categories, setCategories, fetchCategories } = useAdminAuth();
  const [editMainCategoryId, setEditMainCategoryId] = useState(null);
  const [editMainCategoryName, setEditMainCategoryName] = useState("");
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({
    add: false,
    edit: false,
    delete: null,
  });
  
  const adminToken = localStorage.getItem("adminToken");

  useEffect(() => {
    // Reset image preview when form is reset
    if (!mainCategoryName) {
      setMainCategoryImagePreview(null);
    }
  }, [mainCategoryName]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error("Please select a valid image file (JPEG, PNG, GIF, WEBP)");
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image size should be less than 2MB");
      return;
    }
    
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      setMainCategoryImage(reader.result);
      setMainCategoryImagePreview(reader.result);
    };
    reader.onerror = () => {
      toast.error("Error reading file");
    };
  };

  const handleMainCategorySubmit = async (e) => {
    e.preventDefault();
    
    if (!mainCategoryName.trim()) {
      toast.error("Category name cannot be empty");
      return;
    }
    
    if (!mainCategoryImage) {
      toast.error("Please select an image for the category");
      return;
    }
    
    try {
      setActionLoading({ ...actionLoading, add: true });
      
      const payload = {
        name: mainCategoryName.trim(),
        base64Image: mainCategoryImage,
      };
      
      const response = await fetch(
        "https://admin.riymay.com/api/v1/admin/create/main/product-category",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify(payload),
        }
      );
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success("Category created successfully");
        setMainCategoryName("");
        setMainCategoryImage(null);
        setMainCategoryImagePreview(null);
        fetchCategories();
      } else {
        toast.error(data.message || "Failed to create category");
      }
    } catch (error) {
      console.error("Error creating main category:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setActionLoading({ ...actionLoading, add: false });
    }
  };

  const handleEditMainCategory = async (e) => {
    e.preventDefault();
    
    if (!editMainCategoryName.trim()) {
      toast.error("Category name cannot be empty");
      return;
    }
    
    try {
      setActionLoading({ ...actionLoading, edit: true });
      
      const payload = {
        categoryId: editMainCategoryId,
        name: editMainCategoryName.trim(),
      };
      
      const response = await axios.patch(
        `https://admin.riymay.com/api/v1/admin/update/main/product-category`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${adminToken}`,
          },
        }
      );
      
      toast.success("Category updated successfully");
      setEditMainCategoryId(null);
      setEditMainCategoryName("");
      fetchCategories();
    } catch (error) {
      console.error("Error updating main category:", error);
      toast.error("Failed to update category. Please try again.");
    } finally {
      setActionLoading({ ...actionLoading, edit: false });
    }
  };

  const handleDeleteMainCategory = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setActionLoading({ ...actionLoading, delete: id });
          
          const response = await axios.delete(
            `https://admin.riymay.com/api/v1/admin/delete/main/product-category?id=${id}`,
            {
              headers: {
                Authorization: `Bearer ${adminToken}`,
              },
            }
          );
          
          toast.success("Category deleted successfully");
          fetchCategories();
        } catch (error) {
          console.error("Error deleting main category:", error);
          toast.error("Failed to delete category. Please try again.");
        } finally {
          setActionLoading({ ...actionLoading, delete: null });
        }
      }
    });
  };

  return (
    <div className="category-page-container">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <h1 className="page-title">Category Management</h1>
      
      <div className="category-content">
        {/* Form to create main category */}
        <div className="form-section">
          <form className="category-form" onSubmit={handleMainCategorySubmit}>
            <h2 className="form-title">Create Main Category</h2>
            
            <div className="form-group">
              <label htmlFor="categoryName">Category Name</label>
              <input
                id="categoryName"
                type="text"
                placeholder="Enter main category name"
                value={mainCategoryName}
                onChange={(e) => setMainCategoryName(e.target.value)}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="categoryImage">Category Image</label>
              <div className="image-upload-container">
                <label htmlFor="categoryImage" className="image-upload-label">
                  {mainCategoryImagePreview ? (
                    <img 
                      src={mainCategoryImagePreview} 
                      alt="Category preview" 
                      className="image-preview" 
                    />
                  ) : (
                    <div className="upload-placeholder">
                      <FaImage className="upload-icon" />
                      <span className="upload-placeholder-span-text">Select Image</span>
                    </div>
                  )}
                </label>
                <input
                  id="categoryImage"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="image-upload-input"
                  required
                />
              </div>
              <p className="image-requirements">
                Image should be less than 2MB (JPEG, PNG, GIF, WEBP)
              </p>
            </div>
            
            <button 
              type="submit" 
              className="submit-button"
              disabled={actionLoading.add}
            >
              {actionLoading.add ? (
                <ClipLoader size={20} color="#ffffff" />
              ) : (
                <>
                  <FaPlus /> Add Category
                </>
              )}
            </button>
          </form>
        </div>

        {/* Display and manage categories */}
        <div className="category-list-section">
          <h2 className="section-title">Existing Categories</h2>
          
          {loading ? (
            <div className="loading-container">
              <ClipLoader size={40} color="#0055ff" />
              <p>Loading categories...</p>
            </div>
          ) : categories.length === 0 ? (
            <div className="no-categories">
              <p>No categories found. Create your first category!</p>
            </div>
          ) : (
            <div className="category-grid">
              <div className="category-grid-header">
                <span>Image</span>
                <span>Category Name</span>
                <span></span>
                <span>Actions</span>
              </div>
              
              {categories.map((category) => (
                <div key={category._id} className="category-grid-row">
                  {/* Image Section */}
                  <div className="category-image">
                    <img
                      src={category.base64Image}
                      alt={`${category.name} image`}
                      className="category-img"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjgwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjZjFmMWYxIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxMiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iIGZpbGw9IiM4ODg4ODgiPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==";
                      }}
                    />
                  </div>
                  
                  {/* Main Category Section */}
                  <div className="main-category">
                    {editMainCategoryId === category._id ? (
                      <div className="edit-category">
                        <input
                          type="text"
                          value={editMainCategoryName}
                          onChange={(e) => setEditMainCategoryName(e.target.value)}
                          className="edit-category-input"
                          placeholder="Enter new category name"
                        />
                        <div className="edit-actions">
                          <button
                            onClick={handleEditMainCategory}
                            className="save-btn"
                            disabled={actionLoading.edit}
                          >
                            {actionLoading.edit ? (
                              <ClipLoader size={16} color="#ffffff" />
                            ) : (
                              <MdSave />
                            )}
                          </button>
                          <button
                            onClick={() => setEditMainCategoryId(null)}
                            className="cancel-btn"
                          >
                            <MdClose />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <h3 className="category-title">{category.name}</h3>
                    )}
                  </div>

                  {/* Spacer for grid layout */}
                  <div className="spacer"></div>

                  {/* Actions Section */}
                  <div className="category-actions">
                    <button
                      className="edit-btn"
                      onClick={() => {
                        setEditMainCategoryId(category._id);
                        setEditMainCategoryName(category.name);
                      }}
                      title="Edit category"
                    >
                      <MdEdit /> Edit
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => handleDeleteMainCategory(category._id)}
                      disabled={actionLoading.delete === category._id}
                      title="Delete category"
                    >
                      {actionLoading.delete === category._id ? (
                        <ClipLoader size={16} color="#ffffff" />
                      ) : (
                        <>
                          <MdOutlineDeleteOutline /> Delete
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Category;